import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { HtmlEditor } from "components/ValidatedFields/HtmlEditor";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import React, { useState } from "react";
import { formatDeadline } from "userTask/beforeShowLabelHelper";
import { DeadlineOptionsDropdownForTemplate, DeadlineTemplateOption } from "userTask/create/DeadlineOptionsDropdown";

interface DefaultTaskDetailSectionProps {
  userTaskTemplate: server.dto.UserTaskTemplate
}

interface UserTaskTemplateState {
  deadline: DeadlineTemplateOption

  title: string;
  body: string;

  reminderPopup: boolean;
  reminderEmail: boolean;
}

export const DefaultTaskDetailSection = (props: DefaultTaskDetailSectionProps) => {
  const [userTaskTemplate, setUserTaskTemplate] = useState<UserTaskTemplateState>({
    deadline: {
      type: props.userTaskTemplate.deadlineType,
      days: props.userTaskTemplate.deadlineOffsetInDays,
      label: formatDeadline(props.userTaskTemplate.deadlineType, props.userTaskTemplate.deadlineOffsetInDays)
    },

    title: props.userTaskTemplate.title,
    body: props.userTaskTemplate.body,
    reminderPopup: props.userTaskTemplate.reminderPopupDefault,
    reminderEmail: props.userTaskTemplate.reminderEmailDefault,
  });

  const onValueChanged = (key: string, value: any) => {
    if (key === "deadline") {
      setUserTaskTemplate({ ...userTaskTemplate, deadline: value });
    } else if (key === "title") {
      setUserTaskTemplate({ ...userTaskTemplate, title: value });
    } else if (key === "body") {
      setUserTaskTemplate({ ...userTaskTemplate, body: value });
    } else if (key === "reminderPopup") {
      setUserTaskTemplate({ ...userTaskTemplate, reminderPopup: value });
    } else if (key === "reminderEmail") {
      setUserTaskTemplate({ ...userTaskTemplate, reminderEmail: value });
    }
  }

  const getUpdateDefaultTaskInstruction = (t: UserTaskTemplateState): server.dto.UpdateUserTaskTemplateInstruction => ({
    body: t.body,
    deadlineOffsetInDays: t.deadline.days,
    deadlineType: t.deadline.type,
    reminderEmailDefault: t.reminderEmail,
    reminderPopupDefault: t.reminderPopup,
    title: t.title,
    userTaskTemplateId: props.userTaskTemplate.userTaskTemplateId
  });

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      type: "regular",
      key: "defaultTask",
      title: RESX.Task.DefaultTask,
      fields: [{
        key: "deadline",
        label: RESX.Message.Deadline,
        render: () => <div>{userTaskTemplate.deadline.label}</div>,
        inPageEdit: {
          render: ({ value, onChange }) => <DeadlineOptionsDropdownForTemplate
            value={value}
            onChange={v => onChange(v, true)}
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          type: "custom",
          initialValue: userTaskTemplate.deadline,
          fullWidth: true,
          submitChange: (deadline: DeadlineTemplateOption) => window.CoreApi.UserTaskTemplate.Update(
            getUpdateDefaultTaskInstruction({
              ...userTaskTemplate,
              deadline,
            })
          )
        }
      }, {
        key: "title",
        label: RESX.Message.Subject,
        render: () => <div>{userTaskTemplate.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.userTask.maxTitleLength}
            value={value}
            onChange={(internalName) => { onChange(internalName); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: userTaskTemplate.title,
          submitChange: (title: string) => window.CoreApi.UserTaskTemplate.Update(
            getUpdateDefaultTaskInstruction({
              ...userTaskTemplate,
              title
            })
          )
        }
      }, {
        key: "body",
        label: RESX.Message.Body,
        isEmpty: !userTaskTemplate.body,
        render: () => <div dangerouslySetInnerHTML={{
          __html: userTaskTemplate.body
        }} />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<string>) => <HtmlEditor
            toolbar="Normal"
            content={value}
            maxContentLength={serverReference.constants.userTask.maxBodyLength}
            focusOnMount
            onChange={(newValue: string) => onChange(newValue)}
            onCtrlEnter={(newValue) => onChange(newValue, true)}
            onEsc={() => cancel()}
          />,
          disableClickOutside: true,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: userTaskTemplate.body,
          submitChange: (body: string) => window.CoreApi.UserTaskTemplate.Update(
            getUpdateDefaultTaskInstruction({
              ...userTaskTemplate,
              body
            })
          )
        }
      }, {
        key: "notificationLabel",
        label: "",
        hideLabel: true,
        render: () => <h3>{RESX.Notifications.resxNotifications}</h3>
      }, {
        key: "reminderPopup",
        label: RESX.Message.ReminderInPopup,
        render: () => <YesNoText value={userTaskTemplate.reminderPopup} />,
        inPageEdit: {
          render: ({ value, onChange }) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          type: "custom",
          initialValue: userTaskTemplate.reminderPopup,
          submitChange: (reminderPopup: boolean) => window.CoreApi.UserTaskTemplate.Update(
            getUpdateDefaultTaskInstruction({
              ...userTaskTemplate,
              reminderPopup
            })
          )
        }
      }, {
        key: "reminderEmail",
        label: RESX.Message.ReminderByEmail,
        render: () => <YesNoText value={userTaskTemplate.reminderEmail} />,
        inPageEdit: {
          render: ({ value, onChange }) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          type: "custom",
          initialValue: userTaskTemplate.reminderEmail,
          submitChange: (reminderEmail: boolean) => window.CoreApi.UserTaskTemplate.Update(
            getUpdateDefaultTaskInstruction({
              ...userTaskTemplate,
              reminderEmail
            })
          )
        }
      }]
    }]}
  />
}